export const SOURCE_SHEETS = [
  {
    id: "the-covenant",
    number: "01",
    title: "The Covenant in Genesis",
    subtitle: "Where the Seven Laws begin",
    intro:
      "Everything the tradition says about the Noahide laws grows from a handful of verses in Genesis. Read them slowly. The Sages found in these words the whole architecture of a universal code.",
    passages: [
      {
        ref: "Genesis 2:16",
        label: "The first command",
        commentary:
          "One verse, addressed to one human being. The Talmud (Sanhedrin 56b) reads each word as a heading: 'commanded' — courts of justice; 'the Lord' — blasphemy; 'G-d' — idolatry; 'the man' — murder; 'saying' — forbidden relations; 'of every tree you may eat' — but not what is stolen. Six laws, before there was a Jew in the world.",
      },
      {
        ref: "Genesis 9:1-7",
        label: "The blessing of Noah",
        commentary:
          "The world begins again. G-d blesses Noah with the same words He gave Adam, permits meat for the first time — and immediately sets two limits: not flesh from a living animal, and not the blood of a human being, 'for in the image of G-d He made man'. This is the seventh law, and the reason behind all of them.",
      },
      {
        ref: "Genesis 9:8-17",
        label: "The sign of the rainbow",
        commentary:
          "The covenant is explicitly with 'every living creature… for all generations'. It is the only covenant in the Torah made with all humanity, and its sign is set in the sky where everyone can see it. The arcs you see across this site are a quiet reminder of it.",
      },
      {
        ref: "Genesis 18:19",
        label: "Why Abraham was chosen",
        commentary:
          "Not for his own sake, but 'so that he will command his children… to do righteousness and justice'. Abraham is the Torah's model for the nations: faith in the One G-d expressed as hospitality, honesty and the pursuit of justice. The Noahide path walks in his footsteps.",
      },
    ],
  },
  {
    id: "the-talmud",
    number: "02",
    title: "The Talmud, Sanhedrin 56–59",
    subtitle: "Where the laws are named and derived",
    intro:
      "The seventh chapter of tractate Sanhedrin contains the classical discussion of the Noahide laws. These are the passages every serious student should know — presented in the original Aramaic-Hebrew with the Steinsaltz English.",
    passages: [
      {
        ref: "Sanhedrin 56a:24",
        label: "The baraita of the seven",
        commentary:
          "The foundational text. 'The Sages taught: the descendants of Noah were commanded seven mitzvot.' Note the order — courts first — and that the list is a teaching received, not invented.",
      },
      {
        ref: "Sanhedrin 56b:4",
        label: "Rabbi Yoḥanan's derivation",
        commentary:
          "Here the Talmud shows how all six Adamic laws are read out of Genesis 2:16. Whether or not you find each derivation intuitive, notice the method: the Sages insist that these laws are rooted in Scripture, not in human convention.",
      },
      {
        ref: "Sanhedrin 59a:4-5",
        label: "A gentile who studies Torah",
        commentary:
          "Rabbi Meir: even a gentile who engages in Torah is like a High Priest, because the verse says 'which a person shall do and live by them' — a person, not only a priest or an Israelite. The Talmud clarifies: this refers to study of the laws that apply to him — which is exactly what you are doing now.",
      },
    ],
  },
  {
    id: "the-rambam",
    number: "03",
    title: "Rambam, Laws of Kings 8–10",
    subtitle: "The classical codification",
    intro:
      "Maimonides (1138–1204) gathered the scattered Talmudic discussions into three orderly chapters at the end of his Mishneh Torah. These are the key paragraphs — the clearest statement of what the Noahide path is, who walks it, and what it promises.",
    passages: [
      {
        ref: "Mishneh Torah, Kings and Wars 8:10",
        label: "Moses commanded to teach the nations",
        commentary:
          "The Noahide laws are not merely 'natural law' that humanity worked out for itself. The Rambam records that Moses was commanded by G-d to bring all the inhabitants of the world to accept them. Teaching them is a Torah mandate.",
      },
      {
        ref: "Mishneh Torah, Kings and Wars 8:11",
        label: "The pious of the nations",
        commentary:
          "The most quoted line in Noahide literature. Two things stand out: the promise — a share in the World to Come — and the condition — accepting the laws because G-d commanded them in the Torah through Moses. Observance from conviction, not merely from reason.",
      },
      {
        ref: "Mishneh Torah, Kings and Wars 9:1",
        label: "Six to Adam, one to Noah",
        commentary:
          "The Rambam's crisp summary of the history: six commandments to the first human, the seventh added after the Flood, and the whole 'tradition from Moses our teacher'.",
      },
      {
        ref: "Mishneh Torah, Kings and Wars 9:14",
        label: "The obligation of courts",
        commentary:
          "How the positive commandment works in practice: judges in every district, judging the other six. The Rambam's explanation of the Shechem episode (Genesis 34) shows how seriously he took the duty not to look away.",
      },
      {
        ref: "Mishneh Torah, Kings and Wars 10:10",
        label: "Other commandments a Noahide may keep",
        commentary:
          "A door opened wide: a gentile who wishes to perform other mitzvot of the Torah for the sake of reward 'is not prevented'. Charity, kindness, honouring parents, study — all are welcome. Note the exceptions discussed by the commentators (such as full Sabbath observance).",
      },
    ],
  },
  {
    id: "the-prophets",
    number: "04",
    title: "The Prophets and Psalms",
    subtitle: "The nations in G-d's future",
    intro:
      "The Noahide path is not a consolation prize. The prophets describe a world in which all peoples know and serve G-d — and the Psalms give that hope a voice anyone can pray.",
    passages: [
      {
        ref: "I Kings 8:41-43",
        label: "Solomon prays for the stranger",
        commentary:
          "At the dedication of the Temple, Solomon asks G-d to hear 'the foreigner who is not of Your people Israel'. The Temple was always meant to be 'a house of prayer for all peoples'. Your prayer is expected.",
      },
      {
        ref: "Isaiah 11:9",
        label: "Full of the knowledge of the Lord",
        commentary:
          "The Rambam closes his entire code with this verse. The goal of history is not that everyone becomes Jewish, but that everyone knows G-d — 'as the waters cover the sea'.",
      },
      {
        ref: "Micah 4:1-4",
        label: "Many nations shall go up",
        commentary:
          "The nations come to learn G-d's ways and walk in His paths; swords become ploughshares. The Noahide movement sees itself as an early sign of this.",
      },
      {
        ref: "Zephaniah 3:9",
        label: "A pure language",
        commentary:
          "'Then I will turn to the peoples a pure language, that they may all call upon the Name of the Lord, to serve Him with one accord.' Unity of service, not uniformity of identity.",
      },
      {
        ref: "Psalms 145:9",
        label: "His mercies are over all His works",
        commentary:
          "A one-line theology of the Noahide path: G-d's goodness is universal. This Psalm is recited three times daily in Jewish prayer and is a good first Psalm for anyone to learn.",
      },
    ],
  },
];

export const ESSAYS = [
  {
    slug: "why-seven",
    title: "Why Seven?",
    subtitle: "The shape of a universal code",
    readingTime: "6 min",
    sections: [
      {
        heading: "A number that keeps returning",
        paragraphs: [
          "Seven days of creation. Seven colours in the rainbow that sealed the covenant with Noah. Seven laws for the children of Noah. In the Torah's vocabulary, seven is the number of completeness within the natural world — the full cycle of time, the whole spectrum of light. When the Sages speak of seven laws, they are not counting an arbitrary list. They are saying: this is a complete framework for human life under heaven.",
          "Compare the 613 commandments given to Israel. That number, the Sages taught, corresponds to the 365 days of the solar year and the 248 limbs of the body — the total saturation of time and self with Divine service. Israel's covenant is a priestly one, intense and particular. The Noahide covenant is universal, and its number signals something different: not saturation but foundation.",
        ],
      },
      {
        heading: "Six boundaries and one task",
        paragraphs: [
          "Look at the structure. Six of the seven laws are prohibitions — fences around the great dangers: false worship, contempt for G-d, violence, theft, sexual chaos, cruelty to creatures. One is positive: build justice. The pattern mirrors creation itself, where six days of work are followed by a seventh that gives them meaning.",
          "The Rambam (Laws of Kings 9:1) records that six were given to Adam and the seventh — flesh from the living — to Noah. Yet the Talmud lists courts of justice first. There is a lesson in the tension. Chronologically, the laws begin with the individual: what you may not do. Logically, they culminate in society: what you must build together. A person who keeps the six alone in a cave has not fulfilled the seven.",
        ],
      },
      {
        heading: "Headings, not a checklist",
        paragraphs: [
          "Each of the seven is a heading over a whole domain. 'Theft' includes wages, fraud, kidnapping and false weights. 'Courts' includes an entire civil law. 'Idolatry' includes the whole life of faith. This is why the Sages could say that the seven laws contain many details, and why serious students spend years on them.",
          "The number seven, then, is not a limit but a map. It tells you where the territory lies: your relationship with G-d (idolatry, blasphemy), with other people (murder, theft, relations), and with the world you have been given (creatures, society). Learn the map well, and you will know where you are standing at every moment of an ordinary day.",
        ],
      },
    ],
  },
  {
    slug: "the-image-of-god",
    title: "The Image of G-d",
    subtitle: "The idea beneath every law",
    readingTime: "7 min",
    sections: [
      {
        heading: "One sentence in Genesis",
        paragraphs: [
          "'And G-d created man in His image; in the image of G-d He created him; male and female He created them' (Genesis 1:27). No other creature is described this way. Not the angels, not the stars, not the great sea creatures. Only the human being — every human being — carries the tzelem Elokim, the image of G-d.",
          "The Torah does not explain what the image is. The commentators offer answers: the intellect, free will, the capacity for relationship, the power to create and to choose good. Perhaps the Torah's silence is deliberate. The image of G-d is not a feature to be isolated; it is the whole person, seen rightly.",
        ],
      },
      {
        heading: "The reason for the laws",
        paragraphs: [
          "When G-d gives Noah the prohibition of murder, He gives a reason — one of the very few explicit reasons in the Torah's legislation: 'for in the image of G-d He made man' (Genesis 9:6). The sanctity of life is not a social convention. It rests on what a human being is.",
          "Follow the thread and the other laws light up. Theft violates a person's standing as a steward of G-d's world. Forbidden relations treat the bearer of G-d's image as an object. Blasphemy insults the One whose image we carry. Idolatry bows to what is less than human, when the human is the highest image of the Divine on earth. Even the law of courts is an act of reverence: justice is how a society honours the image of G-d in the weak as much as in the strong.",
        ],
      },
      {
        heading: "Including you",
        paragraphs: [
          "It is easy to hear 'the image of G-d' as a statement about others — a reason to treat them well. It is also a statement about you. Your life is sacred. Your choices matter cosmically. The Sages taught that a single human being is 'an entire world' (Sanhedrin 37a), and that each person is obliged to say: 'for my sake the world was created.' Not from arrogance — from responsibility.",
          "This is why loyalty to oneself belongs beside loyalty to G-d and to the world. To neglect, degrade or despair of oneself is to mishandle an image of G-d. The Noahide path begins with the recognition that there is a Creator, and continues with the recognition that He has left His likeness in your care.",
        ],
      },
    ],
  },
  {
    slug: "providence",
    title: "Providence and Free Will",
    subtitle: "How G-d runs the world — and leaves room for you",
    readingTime: "8 min",
    sections: [
      {
        heading: "Two truths held together",
        paragraphs: [
          "Jewish thought insists on two things that seem to pull against each other. First: G-d knows and governs all things. 'His mercies are over all His works' (Psalms 145:9); not a sparrow falls without His knowledge. Second: human beings are genuinely free. 'See, I have set before you today life and good, death and evil… choose life' (Deuteronomy 30:15, 19).",
          "The Rambam (Laws of Repentance 5:1–4) makes free will the cornerstone of the entire Torah. If a person were not free, he writes, commandment, reward and punishment would be meaningless. Whatever else is true about G-d's knowledge, the Torah's demand that you choose is not a fiction.",
        ],
      },
      {
        heading: "Providence in practice",
        paragraphs: [
          "What does it mean to trust in Divine providence (hashgachah) without becoming passive? The tradition's answer is the farmer who ploughs, sows and waters — and then prays for rain. Effort is a commandment; outcome belongs to G-d. 'Many are the plans in a person's heart, but it is the counsel of the Lord that will stand' (Proverbs 19:21).",
          "For the Noahide, this bears directly on the first two laws. Idolatry, at root, is the attempt to control outcome through other powers — charms, stars, spirits. Blasphemy is the refusal to accept outcome from G-d's hand. Trust is the alternative to both: doing what is yours to do, and resting in the knowledge that the rest is held by One who is good.",
        ],
      },
      {
        heading: "Suffering and the limits of understanding",
        paragraphs: [
          "No honest account of providence can skip the hardest question. Why do good people suffer? The Torah's longest meditation on this — the book of Job — ends not with an answer but with an encounter. Job is not told why; he is shown Who. The Sages were equally reticent: 'It is not in our power to explain either the tranquillity of the wicked or the suffering of the righteous' (Avot 4:15).",
          "What the tradition does offer is a posture: lament is permitted, bitterness is not; questions are welcome, accusation is not; and the final word belongs to a G-d whose goodness the Psalmist was willing to assert even from the depths. Faith, in this account, is not certainty about the mechanism. It is loyalty in the dark.",
        ],
      },
    ],
  },
  {
    slug: "repentance",
    title: "Repentance",
    subtitle: "Teshuvah — the way back is always open",
    readingTime: "6 min",
    sections: [
      {
        heading: "A gift built into creation",
        paragraphs: [
          "The Sages taught that repentance was created before the world itself. The meaning is simple and radical: a world with free human beings would be unbearable without a way to undo wrong. Teshuvah — literally 'return' — is not an afterthought. It is a structural feature of reality.",
          "And it is universal. The clearest biblical story of repentance is not about Israel at all. It is Nineveh — a gentile city — hearing Jonah's warning, turning from violence, and being spared. 'G-d saw their deeds, that they had turned from their evil way' (Jonah 3:10). The book is read in synagogues on the holiest afternoon of the Jewish year precisely because it shows that teshuvah belongs to everyone.",
        ],
      },
      {
        heading: "What repentance is",
        paragraphs: [
          "The Rambam (Laws of Repentance 2:1–2) describes it in steps: stop the wrong; regret it honestly; confess it before G-d in words; and resolve not to return to it — so firmly that 'He who knows all hidden things can testify that this person will not do this sin again.' Complete teshuvah is tested when the same opportunity arises and one refrains.",
          "Note what is absent: no intermediary, no sacrifice, no ritual act. For wrongs against G-d, the path is directly to Him. For wrongs against another person, the Rambam is equally clear: one must first make amends and seek that person's forgiveness. G-d does not forgive on another person's behalf.",
        ],
      },
      {
        heading: "Living with a past",
        paragraphs: [
          "Many who come to the Noahide path carry years of belief and behaviour they now see differently. The tradition's counsel is not shame but return. 'Great is repentance, for it brings healing to the world' (Yoma 86a). Even more striking: the Sages taught that when repentance is done from love, past failings are transformed into merits — because they became the path to G-d.",
          "Practically: begin where you are. Name what needs changing. Speak to G-d about it in your own words. Make right what can be made right with people. Then let it go — 'as far as east is from west, so far has He removed our transgressions from us' (Psalms 103:12). Dwelling in guilt is not humility; it is a failure to take G-d at His word.",
        ],
      },
    ],
  },
  {
    slug: "the-world-to-come",
    title: "The World to Come and the Nations",
    subtitle: "What the Torah promises every righteous person",
    readingTime: "7 min",
    sections: [
      {
        heading: "The righteous of all nations",
        paragraphs: [
          "The Tosefta (Sanhedrin 13:2) records a debate. Rabbi Eliezer held that the nations have no share in the World to Come; Rabbi Yehoshua disagreed, citing the verse 'the wicked shall return to Sheol, all the nations that forget G-d' — only those who forget G-d. The law follows Rabbi Yehoshua, and the Rambam codifies it twice: 'the pious of the nations of the world have a share in the World to Come' (Laws of Repentance 3:5; Laws of Kings 8:11).",
          "This is a remarkable thing for a particular people to say about everyone else. Judaism has never taught that one must be Jewish to be saved. What it has taught is that one must be righteous — and it has defined righteousness for the nations with unusual precision: the Seven Laws, kept because G-d commanded them.",
        ],
      },
      {
        heading: "What 'the World to Come' means",
        paragraphs: [
          "The Rambam (Laws of Repentance ch. 8) describes the World to Come as a purely spiritual existence in which the soul delights in the knowledge of G-d — 'the righteous sit with their crowns on their heads and enjoy the radiance of the Divine Presence.' There is no eating, no drinking, no bodily life. It is the fulfilment of what the soul was made for.",
          "Alongside this stands the Messianic era — a time within history when the world is set right, when 'nation shall not lift up sword against nation' (Micah 4:3) and 'the earth shall be full of the knowledge of the Lord' (Isaiah 11:9). The Rambam insists the Messianic era is natural: no change in the laws of nature, only a world that finally lives by G-d's will. The nations are fully part of that picture.",
        ],
      },
      {
        heading: "Why it matters now",
        paragraphs: [
          "Knowing that the door is open changes how one walks. The Noahide need not live in anxiety about status or in envy of a covenant that was never asked of him. His task is clear and his reward is assured: 'anyone who accepts the seven commandments and is careful to observe them… has a share in the World to Come.'",
          "And there is a role in history to play. The prophets' vision of all nations knowing G-d does not happen by magic. It happens as individuals, one at a time, in ordinary places, decide to live by the truth they have recognised. Every Noahide household is a small outpost of that future. The Lubavitcher Rebbe, who did more than anyone in the modern era to revive this path, insisted that the nations' embrace of the Seven Laws is itself a sign of the Messianic age drawing near.",
        ],
      },
    ],
  },
];


/** Which study materials each law page should point to. */
export const LAW_STUDY = {
  "prohibition-of-idolatry": { sheet: "the-rambam", essays: ["the-image-of-god", "providence"] },
  "prohibition-of-blasphemy": { sheet: "the-prophets", essays: ["providence", "repentance"] },
  "prohibition-of-murder": { sheet: "the-covenant", essays: ["the-image-of-god"] },
  "prohibition-of-theft": { sheet: "the-talmud", essays: ["why-seven", "repentance"] },
  "prohibition-of-illicit-relations": { sheet: "the-covenant", essays: ["repentance", "the-image-of-god"] },
  "prohibition-of-eating-living-flesh": { sheet: "the-covenant", essays: ["why-seven"] },
  "establishment-of-courts-of-justice": { sheet: "the-rambam", essays: ["why-seven", "the-world-to-come"] },
};
